import { client } from '@/services/api/axios.ts';
import { QueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

type AssignUsersToRuleResponse = {
  success: boolean;
  message?: string;
};

export const usePostRuleAssignments = ({
  queryClient,
  ruleId,
  onSuccess,
}: {
  queryClient: QueryClient;
  ruleId: string;
  onSuccess?: (data: AssignUsersToRuleResponse) => void;
}) => {
  return useMutation({
    mutationFn: async ({ ruleId, userIds }: { ruleId: string; userIds: string[] }) => {
      const postPromise = client.post<AssignUsersToRuleResponse>(
        `/notifications/rules/${ruleId}/assignments`,
        { userIds }
      );

      const { data } = await toast.promise(postPromise, {
        loading: 'Assigning users to rule...',
        success: 'Users assigned to rule successfully',
        error: (_) => {
          return 'Failed to assign users to rule';
        },
      });

      return data;
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onMutate: async ({ userIds }) => {
      const queryKey = ['notifications', 'rules', ruleId, 'assignments'];

      await queryClient.cancelQueries({ queryKey });

      const previousUserIds = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, () => ({ userIds }));

      return { previousUserIds };
    },
    onError: (_, __, context) => {
      if (context?.previousUserIds) {
        queryClient.setQueryData(
          ['notifications', 'rules', ruleId, 'assignments'],
          context.previousUserIds
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'rules'] });
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'rules', ruleId, 'assignments'],
      });
    },
  });
};
